/**
 * Made For You — Shopify Theme Extension (Horizon, V2 catalog)
 *
 * Reads ?mfy=SLUG from URL, fetches personalized art session from our API,
 * and applies customer-specific personalization across the storefront.
 *
 * V2 architecture (rewrite of S53 — old script targeted the V1 per-style
 * product split, see docs/theme-extension-state.md):
 *
 *   • Single product per type. Shopify product handles are dad-hat, hoodie,
 *     crewneck, mug, phone-case. There is no style suffix in the handle.
 *   • Each product type has ONE designated style, defined in
 *     src/lib/art-generator.ts::PRODUCT_STYLE_MAP. Theme extension owns
 *     its own copy of this map (it runs in the browser).
 *
 * Install: as a snippet `snippets/made-for-you.liquid` rendered from
 * `layout/theme.liquid` just before </body>. See theme-extension-install.md.
 */
(function () {
  'use strict';

  var API_BASE = 'https://made-for-you.vercel.app';
  var SESSION_KEY = 'mfy_session';

  // Source of truth: src/lib/art-generator.ts::PRODUCT_STYLE_MAP
  // Keys are template slugs (matches DB product_templates.slug + session.mockups keys).
  var TEMPLATE_TO_STYLE = {
    hat: 'embroidery-heirloom',
    hoodie: 'embroidery-outline',
    crewneck: 'embroidery-heirloom',
    mug: 'watercolor',
    'phone-case': 'watercolor'
  };

  // Shopify product handles → internal template slugs. The Shopify catalog
  // titles "Dad Hat" and "Phone Case" become handles "dad-hat" and "phone-case";
  // the rest are identical.
  var HANDLE_TO_TEMPLATE = {
    'dad-hat': 'hat',
    'hoodie': 'hoodie',
    'crewneck': 'crewneck',
    'mug': 'mug',
    'phone-case': 'phone-case'
  };

  // ── Session Management ──

  function getSlug() {
    var params = new URLSearchParams(window.location.search);
    var slug = params.get('mfy');
    if (slug) {
      sessionStorage.setItem('mfy_slug', slug);
      return slug;
    }
    return sessionStorage.getItem('mfy_slug');
  }

  function saveSession(data) {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(data)); } catch (e) { /* ignore */ }
  }

  function loadSession() {
    try {
      var raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function fetchSession(slug) {
    return fetch(API_BASE + '/api/storefront/session?slug=' + encodeURIComponent(slug) + '&v=4')
      .then(function (res) {
        if (!res.ok) throw new Error('Session API ' + res.status);
        return res.json();
      })
      .then(function (data) {
        saveSession(data);
        return data;
      });
  }

  // ── Product / Style Resolution ──

  /** Map a Shopify product handle (e.g. "dad-hat") to a template slug. */
  function handleToTemplate(handle) {
    if (!handle) return null;
    if (HANDLE_TO_TEMPLATE[handle]) return HANDLE_TO_TEMPLATE[handle];
    // Permissive fallback: if the handle exactly matches a known template slug
    // (e.g. someone renames "dad-hat" → "hat"), accept it.
    return TEMPLATE_TO_STYLE[handle] ? handle : null;
  }

  /** Read the URL handle for the current product page. */
  function currentProductHandle() {
    var m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  /** Look up the best personalized image URL for (templateSlug, style). */
  function lookupImageUrl(session, templateSlug, style) {
    if (
      templateSlug &&
      session.mockups &&
      session.mockups[templateSlug] &&
      session.mockups[templateSlug][style]
    ) {
      return session.mockups[templateSlug][style];
    }
    if (session.styles && session.styles[style] && session.styles[style].displayUrl) {
      return session.styles[style].displayUrl;
    }
    return null;
  }

  // ── Page-Type Detection ──

  function isProductPage() { return /\/products\//.test(location.pathname); }
  function isCollectionPage() { return /\/collections\//.test(location.pathname); }
  function isCartPage() { return /\/cart/.test(location.pathname); }

  // ── Image Swap Helpers ──

  function replaceImgSrc(img, newSrc) {
    if (!img || !newSrc || img.dataset.mfySwapped === newSrc) return;
    img.src = newSrc;
    img.srcset = '';
    img.removeAttribute('srcset');
    img.dataset.mfySwapped = newSrc;
  }

  // ── Product Page Swap ──

  function swapProductPage(session, templateSlug, style) {
    var newSrc = lookupImageUrl(session, templateSlug, style);
    if (!newSrc) return;

    var imgs = document.querySelectorAll('.product-media__image');
    for (var i = 0; i < imgs.length; i++) replaceImgSrc(imgs[i], newSrc);

    var stickyImg = document.querySelector('.sticky-add-to-cart__image-img');
    if (stickyImg) replaceImgSrc(stickyImg, newSrc);
  }

  // ── Collection Page Swap ──

  function swapCollectionPage(session) {
    var seen = {};
    var anchors = document.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < anchors.length; i++) {
      var anchor = anchors[i];
      var href = anchor.getAttribute('href') || '';
      var match = href.match(/\/products\/([^/?#]+)/);
      if (!match) continue;
      var templateSlug = handleToTemplate(match[1]);
      if (!templateSlug) continue;
      var style = TEMPLATE_TO_STYLE[templateSlug];
      if (!style) continue;
      var key = templateSlug + '|' + style;
      if (seen[key]) continue;
      seen[key] = true;
      var url = lookupImageUrl(session, templateSlug, style);
      if (!url) continue;
      // Horizon: img lives outside the anchor — climb to <product-card> root
      var container = anchor.closest('product-card') || anchor.parentElement;
      var img = container ? container.querySelector('img') : null;
      if (img) replaceImgSrc(img, url);
    }
  }

  // ── Cart Page Swap ──

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .catch(function () { return null; });
  }

  function swapCartPage(session) {
    fetchCart().then(function (cart) {
      if (!cart || !cart.items) return;
      var rows = document.querySelectorAll('a[href*="/products/"]');
      for (var r = 0; r < rows.length; r++) {
        var anchor = rows[r];
        var href = anchor.getAttribute('href') || '';
        var m = href.match(/\/products\/([^/?#]+)/);
        if (!m) continue;
        var templateSlug = handleToTemplate(m[1]);
        if (!templateSlug) continue;
        var style = TEMPLATE_TO_STYLE[templateSlug];
        if (!style) continue;
        for (var c = 0; c < cart.items.length; c++) {
          var item = cart.items[c];
          var props = item.properties || {};
          if (
            (item.handle && item.handle === m[1]) ||
            (props._shop_slug === session.shopSlug && (props._style === style || props.style === style))
          ) {
            var url = lookupImageUrl(session, templateSlug, style);
            if (url) {
              var img = anchor.querySelector('img') || anchor.parentElement.querySelector('img');
              if (img) replaceImgSrc(img, url);
            }
            break;
          }
        }
      }
    });
  }

  // ── Line Item Property Injection (product page) ──

  function injectProperties(session, templateSlug, style) {
    var styleData = session.styles && session.styles[style];
    var forms = document.querySelectorAll('form[action="/cart/add"], form[data-type="add-to-cart-form"]');

    // Prefer the per-product Satori composition (pet name + face crop already
    // baked in). Falls back to the raw-art print file URL so orders still
    // fulfill if compositions weren't rendered.
    var composedUrl = null;
    if (
      templateSlug &&
      session.compositions &&
      session.compositions[templateSlug] &&
      session.compositions[templateSlug][style]
    ) {
      composedUrl = session.compositions[templateSlug][style];
    }
    var printFileUrl = composedUrl || (styleData ? styleData.printFileUrl : '');

    var properties = {
      _print_file_url: printFileUrl,
      Style: style.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); }),
      'Pet Name': session.petName,
      _shop_slug: session.shopSlug,
      _style: style
    };

    for (var f = 0; f < forms.length; f++) {
      var form = forms[f];
      var keys = Object.keys(properties);
      for (var k = 0; k < keys.length; k++) {
        var name = 'properties[' + keys[k] + ']';
        var input = form.querySelector('input[name="' + name + '"]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          form.appendChild(input);
        }
        input.value = properties[keys[k]];
      }
    }
  }

  // ── Personalization Banner ──
  //
  // Warm MFY palette: cream surface, terracotta accent, near-black text. No
  // emoji decoration — the brand voice is editorial, not sparkly.

  function showBanner(petName) {
    if (document.getElementById('mfy-banner')) return;
    var banner = document.createElement('div');
    banner.id = 'mfy-banner';
    banner.style.cssText = [
      'background:#FAF8F5',
      'color:#0F0F0E',
      'text-align:center',
      'padding:12px 16px',
      'font-size:13px',
      'font-family:Georgia,"Lora","Cormorant Garamond",serif',
      'font-style:italic',
      'letter-spacing:0.01em',
      'border-bottom:1px solid rgba(15,15,14,0.08)',
      'position:relative',
      'z-index:1000'
    ].join(';');
    var label = document.createTextNode('shopping ');
    var nameEl = document.createElement('strong');
    nameEl.style.fontWeight = '600';
    nameEl.textContent = petName.toLowerCase() + '’s';
    var suffix = document.createTextNode(' custom collection');
    banner.appendChild(label);
    banner.appendChild(nameEl);
    banner.appendChild(suffix);
    document.body.prepend(banner);
  }

  // ── Main ──

  function apply(session) {
    showBanner(session.petName);

    if (isProductPage()) {
      var handle = currentProductHandle();
      var templateSlug = handleToTemplate(handle);
      if (!templateSlug) return;
      var style = TEMPLATE_TO_STYLE[templateSlug];
      if (!style) return;
      swapProductPage(session, templateSlug, style);
      injectProperties(session, templateSlug, style);
    } else if (isCollectionPage()) {
      swapCollectionPage(session);
    } else if (isCartPage()) {
      swapCartPage(session);
    }
  }

  function init() {
    var slug = getSlug();
    if (!slug) return;

    var cached = loadSession();
    if (cached && cached.shopSlug === slug) {
      apply(cached);
    }

    fetchSession(slug)
      .then(function (session) { apply(session); })
      .catch(function (err) { console.warn('[MFY] Session fetch failed:', err.message); });
  }

  // ── MutationObserver for AJAX navigation (Horizon uses section rendering) ──

  var debounceTimer;
  function onMutation() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      var slug = getSlug();
      if (!slug) return;
      var session = loadSession();
      if (session) apply(session);
    }, 200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  var observer = new MutationObserver(onMutation);
  observer.observe(document.body, { childList: true, subtree: true });
})();
